import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  Share2,
  Copy,
  Check,
  X,
  Calendar,
  Info,
  Link2,
  Unlink,
} from 'lucide-react';
import { currentUser } from '~/lib/pb';
import { canOperateCrm } from '~/lib/permissions';
import { useActiveTenantId, useTenantContext } from '~/lib/tenantContext';
import {
  useBookingPages,
  useDeleteBookingPage,
  useMyGoogleStatus,
  useGoogleOAuthStart,
  useGoogleOAuthDisconnect,
  type BookingPage,
} from '~/lib/booking-queries';
import { BookingCalendar } from '~/components/BookingCalendar/BookingCalendar';
import { CreateBookingModal } from '~/components/BookingCalendar/CreateBookingModal';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import styles from './_authed.agenda.module.css';

const helpIconStyle: React.CSSProperties = {
  display: 'inline-flex',
  verticalAlign: 'middle',
  marginLeft: 6,
  color: 'var(--color-text-subtle)',
  cursor: 'help',
};

export const Route = createFileRoute('/_authed/agenda')({
  component: AgendaAdmin,
  validateSearch: (s: Record<string, unknown>) => ({
    google: typeof s.google === 'string' ? s.google : undefined,
    reason: typeof s.reason === 'string' ? s.reason : undefined,
  }),
});

function AgendaAdmin() {
  const me = currentUser();
  const tenantId = useActiveTenantId();
  const { selectedTenant } = useTenantContext();
  const isSuperadmin = me?.role === 'superadmin';
  const { data: pages, isLoading } = useBookingPages(tenantId);
  const { data: googleStatus } = useMyGoogleStatus();
  const [sharing, setSharing] = useState<BookingPage | null>(null);
  const [creating, setCreating] = useState(false);
  const del = useDeleteBookingPage();
  const confirm = useConfirm();
  const search = useSearch({ from: '/_authed/agenda' });
  const navigate = useNavigate();
  const [oauthBanner, setOauthBanner] = useState<'connected' | 'error' | null>(null);

  useEffect(() => {
    if (search.google === 'connected' || search.google === 'error') {
      setOauthBanner(search.google);
      // Use public path `/agenda` — `from: '/_authed/agenda'` (route id) can rewrite
      // the URL bar to `/_authed/agenda` and TanStack then shows Not Found.
      navigate({ to: '/agenda', search: {}, replace: true });
    }
  }, [search.google, navigate]);

  if (!canOperateCrm(me?.role)) {
    return (
      <div className={styles.page}>
        <p className={styles.notAllowed}>No tienes acceso a la agenda.</p>
      </div>
    );
  }
  if (!tenantId) {
    return (
      <div className={styles.page}>
        <p className={styles.hint}>Selecciona un cliente en el topbar.</p>
      </div>
    );
  }

  async function handleDelete(p: BookingPage) {
    const ok = await confirm({
      title: `Eliminar "${p.name}"`,
      description: (
        <>Se elimina la página de reserva y sus reservas asociadas. Acción irreversible.</>
      ),
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (ok) del.mutate(p.id);
  }

  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">
            Agenda
            <Tooltip
              content={
                isSuperadmin
                  ? 'Páginas de reserva + calendario. Tú configuras las páginas; el cliente conecta Google y ve el calendario.'
                  : 'Tu calendario de reuniones. Conecta Google Calendar si eres anfitrión de una página de reserva.'
              }
            >
              <span style={helpIconStyle}>
                <Info size={14} aria-hidden />
              </span>
            </Tooltip>
          </h1>
        </div>
        {isSuperadmin && (
          <div className="page-header__actions">
            <button className="btn btn--primary" onClick={() => navigate({ to: '/agenda/nueva' })}>
              <Plus size={16} /> Nueva página
            </button>
          </div>
        )}
      </header>

      {oauthBanner === 'connected' && (
        <p className={styles.bannerOk}>
          Google Calendar conectado. Activa la sincronización en cada página de reserva donde seas
          el anfitrión.
        </p>
      )}
      {oauthBanner === 'error' && (
        <p className={styles.bannerErr}>No se pudo conectar Google Calendar. Intenta de nuevo.</p>
      )}

      <MyGoogleCard />

      {isSuperadmin && (
        <>
          <h2 className={styles.sectionTitle}>
            Páginas de reserva
            <Tooltip content="Configura horarios, duración y enlace de cada tipo de reunión. Editar abre la configuración completa.">
              <span style={helpIconStyle}>
                <Info size={14} aria-hidden />
              </span>
            </Tooltip>
          </h2>
          {isLoading && <p className={styles.hint}>Cargando…</p>}
          {!isLoading && (!pages || pages.length === 0) && (
            <div className="empty-state">
              <Calendar className="empty-state__icon" size={28} aria-hidden />
              <p>
                No hay páginas de reserva. Crea la primera con <strong>Nueva página</strong>.
              </p>
            </div>
          )}

          {pages && pages.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Duración</th>
                  <th>Google</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pages.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <button
                        type="button"
                        className={styles.nameLink}
                        onClick={() =>
                          navigate({ to: '/agenda/$pageId', params: { pageId: p.id } })
                        }
                      >
                        <strong>{p.name}</strong>
                      </button>
                      <div className={styles.slug}>
                        /agenda/{selectedTenant?.slug || '…'}/{p.slug}
                      </div>
                    </td>
                    <td>{p.duration_minutes} min</td>
                    <td>
                      <div className={styles.gChips}>
                        {p.sync_google_busy ? (
                          <Tooltip content="Oculta los horarios en que el anfitrión ya está ocupado">
                            <span className={styles.gChip}>Ocupados</span>
                          </Tooltip>
                        ) : null}
                        {p.google_meet_enabled ? (
                          <Tooltip content="Crea evento + Meet al reservar">
                            <span className={styles.gChip}>Meet</span>
                          </Tooltip>
                        ) : null}
                        {!p.sync_google_busy && !p.google_meet_enabled ? (
                          <span className={styles.gChipOff}>Off</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      {p.active ? (
                        <span className={styles.badgeOn}>Activa</span>
                      ) : (
                        <span className={styles.badgeOff}>Inactiva</span>
                      )}
                    </td>
                    <td className={styles.actions}>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        title="Compartir"
                        aria-label={`Compartir ${p.name}`}
                        onClick={() => setSharing(p)}
                      >
                        <Share2 size={15} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        title="Editar"
                        aria-label={`Editar ${p.name}`}
                        onClick={() =>
                          navigate({ to: '/agenda/$pageId', params: { pageId: p.id } })
                        }
                      >
                        <Pencil size={15} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        title="Eliminar"
                        aria-label={`Eliminar ${p.name}`}
                        onClick={() => handleDelete(p)}
                      >
                        <Trash2 size={15} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <section className={styles.calendarSection}>
        <h2 className={styles.sectionTitle}>
          Calendario
          <Tooltip content="Tu agenda confirmada en vista mes / semana / día.">
            <span style={helpIconStyle}>
              <Info size={14} aria-hidden />
            </span>
          </Tooltip>
        </h2>
        <BookingCalendar
          tenantId={tenantId}
          tenantSlug={selectedTenant?.slug}
          tenantTz={selectedTenant?.timezone || 'America/Santiago'}
          onCreate={() => setCreating(true)}
          pages={pages ?? []}
          googleConfigured={!!googleStatus?.configured}
          googleConnected={!!googleStatus?.connected}
        />
      </section>

      {creating && (
        <CreateBookingModal
          pages={pages ?? []}
          tenantTz={selectedTenant?.timezone || 'America/Santiago'}
          onClose={() => setCreating(false)}
          onCreated={() => setCreating(false)}
        />
      )}
      {sharing && isSuperadmin && (
        <SharePanel
          page={sharing}
          tenantSlug={selectedTenant?.slug || ''}
          onClose={() => setSharing(null)}
        />
      )}
    </div>
  );
}

function formatSyncAt(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'hace un momento';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  return new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

function MyGoogleCard() {
  const me = currentUser();
  const isSuperadmin = me?.role === 'superadmin';
  const { data, isLoading, isError, refetch } = useMyGoogleStatus();
  const start = useGoogleOAuthStart();
  const disconnect = useGoogleOAuthDisconnect();
  const confirm = useConfirm();
  const [err, setErr] = useState<string | null>(null);

  async function connect() {
    setErr(null);
    try {
      const { url } = await start.mutateAsync();
      if (url) window.location.href = url;
      else setErr('No se pudo abrir la conexión con Google. Intenta de nuevo en unos minutos.');
    } catch {
      setErr('No se pudo iniciar la conexión con Google.');
    }
  }

  async function doDisconnect() {
    const ok = await confirm({
      title: 'Desconectar Google Calendar',
      description: (
        <>
          Se deja de sincronizar tu calendario. Las páginas de reserva donde eres anfitrión dejarán
          de ocultar tus horarios ocupados y de crear Meet hasta que vuelvas a conectar.
        </>
      ),
      confirmLabel: 'Desconectar',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await disconnect.mutateAsync();
      refetch();
    } catch {
      setErr('No se pudo desconectar.');
    }
  }

  if (!isLoading && data && !data.configured) {
    if (!isSuperadmin) return null;
    return (
      <div className={styles.googleCard}>
        <div className={styles.googleCard__head}>
          <strong>Google Calendar</strong>
          <Tooltip content="Falta GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET en el VPS. Ver docs/BOOKING_V2_GCAL_CHATBOT.md.">
            <span style={helpIconStyle}>
              <Info size={13} aria-hidden />
            </span>
          </Tooltip>
        </div>
        <p className={styles.hint}>Integración pendiente de activar en la plataforma.</p>
      </div>
    );
  }

  return (
    <div className={styles.googleCard}>
      <div className={styles.googleCard__head}>
        <strong>Mi Google Calendar</strong>
        <Tooltip content="Conecta la cuenta de Google del anfitrión. Cada página de reserva usa el calendario de su anfitrión.">
          <span style={helpIconStyle}>
            <Info size={13} aria-hidden />
          </span>
        </Tooltip>
      </div>
      {isLoading && <p className={styles.hint}>Cargando…</p>}
      {!isLoading && (isError || !data) && (
        <p className={styles.hint}>
          No se pudo cargar el estado de Google.{' '}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => refetch()}
            style={{ display: 'inline-flex', marginLeft: 4 }}
          >
            Reintentar
          </button>
        </p>
      )}
      {!isLoading && data?.configured && !data.connected && (
        <div className={styles.googleCard__row}>
          <p className={styles.hint}>Conéctalo si eres anfitrión de alguna página de reserva.</p>
          <button className="btn btn--ghost" onClick={connect} disabled={start.isPending}>
            <Link2 size={14} /> {start.isPending ? 'Abriendo…' : 'Conectar Google'}
          </button>
        </div>
      )}
      {!isLoading && data?.connected && (
        <div className={styles.googleCard__row}>
          <p className={styles.hint}>
            Conectado{data.email ? `: ${data.email}` : ''}
            {data.synced_at ? ` · última sync ${formatSyncAt(data.synced_at)}` : ''}
          </p>
          <button className="btn btn--ghost" onClick={doDisconnect} disabled={disconnect.isPending}>
            <Unlink size={14} /> Desconectar
          </button>
        </div>
      )}
      {err && <p className={styles.errorText}>{err}</p>}
    </div>
  );
}

function SharePanel({
  page,
  tenantSlug,
  onClose,
}: {
  page: BookingPage;
  tenantSlug: string;
  onClose: () => void;
}) {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://app.lifefocus.agency';
  const link = `${origin}/agenda/${tenantSlug}/${page.slug}`;
  const [embedTheme, setEmbedTheme] = useState<'light' | 'dark' | 'auto'>('light');
  const themeQs = embedTheme === 'light' ? '' : 'theme=' + embedTheme;
  const linkWithTheme = themeQs ? `${link}?${themeQs}` : link;
  const iframe = `<div id="focus-agenda-${page.slug}"></div>
<script>
(function(){
  var base=${JSON.stringify(link)};
  var g="", theme=${JSON.stringify(embedTheme === 'light' ? '' : embedTheme)};
  try{var p=new URLSearchParams(location.search);g=p.get("gclid")||"";if(!g){var a=JSON.parse(localStorage.getItem("focus_attr_first")||"{}");g=a.gclid||"";}}catch(e){}
  var q=[];
  if(theme)q.push("theme="+encodeURIComponent(theme));
  if(g)q.push("gclid="+encodeURIComponent(g));
  var f=document.createElement("iframe");
  f.src=base+(q.length?"?"+q.join("&"):"");
  f.width="100%";f.height="720";f.frameBorder="0";f.style.border="0";
  var m=document.getElementById("focus-agenda-${page.slug}");if(m)m.appendChild(f);
})();
</script>`;
  const [copied, setCopied] = useState<'' | 'link' | 'iframe'>('');
  function copy(text: string, which: 'link' | 'iframe') {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(''), 1500);
    });
  }
  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>
            <Share2 size={18} /> Compartir "{page.name}"
          </h2>
          <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Cerrar">
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className={styles.modalBody}>
          <label className={styles.field}>
            <span>Tema del embed</span>
            <select
              className={styles.input}
              value={embedTheme}
              onChange={(e) => setEmbedTheme(e.target.value as 'light' | 'dark' | 'auto')}
            >
              <option value="light">Claro</option>
              <option value="dark">Oscuro</option>
              <option value="auto">Automático (sistema)</option>
            </select>
          </label>
          <label className={styles.field}>
            <span>Enlace directo</span>
            <div className={styles.copyRow}>
              <input
                className={styles.input}
                readOnly
                value={linkWithTheme}
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => copy(linkWithTheme, 'link')}
                aria-label="Copiar enlace"
              >
                {copied === 'link' ? (
                  <Check size={15} aria-hidden />
                ) : (
                  <Copy size={15} aria-hidden />
                )}
              </button>
            </div>
          </label>
          <label className={styles.field}>
            <span>Insertar en el sitio (reenvía gclid)</span>
            <div className={styles.copyRow}>
              <textarea
                className={styles.textarea}
                readOnly
                value={iframe}
                rows={8}
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => copy(iframe, 'iframe')}
                aria-label="Copiar código"
              >
                {copied === 'iframe' ? (
                  <Check size={15} aria-hidden />
                ) : (
                  <Copy size={15} aria-hidden />
                )}
              </button>
            </div>
          </label>
          <a className={styles.openLink} href={linkWithTheme} target="_blank" rel="noreferrer">
            Abrir la página de reserva ↗
          </a>
        </div>
      </div>
    </div>
  );
}
